import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "./icons";

import { cn } from "utils/cn";
import { IconButton } from "./IconButton";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/** Backdrop shared by Dialog and Sheet. */
export const dialogOverlayClasses = cn(
  "fixed inset-0 z-50 bg-overlay",
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-base",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-fast"
);

/** The X button in the top-right corner of a Dialog or Sheet; position it with `className`. */
export const DialogCloseButton: React.FC<{ className?: string }> = ({ className }) => (
  <DialogPrimitive.Close asChild>
    <IconButton
      label="Close"
      icon={<X aria-hidden="true" />}
      size="sm"
      hideTooltip
      className={cn("text-muted-foreground hover:text-foreground", className)}
    />
  </DialogPrimitive.Close>
);

// Durations use the data-state variants so they beat tailwindcss-animate's default 150ms; the
// tokens drop to 0ms under prefers-reduced-motion, which turns the zoom off.
const dialogContentVariants = cva(
  [
    "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col",
    "rounded-card border border-border bg-surface-raised text-foreground shadow-dialog outline-none ease-standard",
    "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-base",
    "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-fast",
    "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
  ],
  {
    variants: {
      size: {
        sm: "max-w-[25rem]",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
      },
    },
    defaultVariants: { size: "md" },
  }
);

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  /** Leave out the built-in close button. */
  hideClose?: boolean;
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, size, hideClose = false, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className={dialogOverlayClasses} />
    <DialogPrimitive.Content
      ref={ref}
      data-ui="dialog"
      className={cn(dialogContentVariants({ size }), className)}
      {...props}
    >
      {children}
      {/* After the children so initial focus lands on the first field, not on Close. */}
      {!hideClose && <DialogCloseButton className="absolute right-4 top-5" />}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = "DialogContent";

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export interface DialogHeaderProps extends Omit<DivProps, "title"> {
  title: React.ReactNode;
  description?: React.ReactNode;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({
  title,
  description,
  className,
  children,
  ...props
}) => (
  <div
    data-ui="dialog-header"
    className={cn("flex flex-col gap-1.5 px-6 pb-5 pt-6 pr-14", className)}
    {...props}
  >
    <DialogPrimitive.Title className="text-lg font-semibold leading-7 tracking-tight text-foreground">
      {title}
    </DialogPrimitive.Title>
    {description && (
      <DialogPrimitive.Description className="text-sm text-muted-foreground">
        {description}
      </DialogPrimitive.Description>
    )}
    {children}
  </div>
);

export const DialogBody = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // The header already leaves a gap below itself.
        "min-h-0 max-h-[70vh] overflow-y-auto px-6 py-5 [[data-ui=dialog-header]+&]:pt-0",
        className
      )}
      {...props}
    />
  )
);
DialogBody.displayName = "DialogBody";

export const DialogFooter: React.FC<DivProps> = ({ className, ...props }) => (
  <div
    className={cn(
      "flex flex-wrap items-center justify-end gap-2 rounded-b-card border-t border-border bg-surface-muted/50 px-6 py-4",
      className
    )}
    {...props}
  />
);
